import AssessmentIcon from "@mui/icons-material/Assessment"
import PaymentIcon from "@mui/icons-material/Payment"
import SpeedIcon from "@mui/icons-material/Speed"
import {
  Alert,
  Box,
  CircularProgress,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material"
import { isUndefined } from "lodash"
import moment from "moment"
import { FC, useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { ModeContext } from "src/ModeContext"
import { useAppSelector } from "../app/hooks"
import { selectMeIsReady } from "../features/me/meSlice"
import OsLayout from "./OsLayout"
import OsReportingKPIs from "./OsReportingKPIs"
import OsReportingReservations from "./OsReportingReservations"
import OsReportingPaymentReservations from "./OsReportingPaymentReservations"

const SubPageTabMap: Record<string, number> = {
  kpis: 0,
  payment: 1,
  reservations: 2,
}

export const OsReportingPage: FC = () => {
  const { t } = useTranslation()

  const isReady = useAppSelector(selectMeIsReady)

  const { subPage = "kpis" } = useParams<{ subPage: string }>()

  const navigate = useNavigate()

  const location = useLocation()

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log("tab change newValue", newValue)
    console.log("tab change locations.search", location.search)
    let locationSearch = location.search
    if (newValue === 2) {
      const filters = {
        dateField: "sst_created",
        startDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
      }
      const searchParams = new URLSearchParams(locationSearch)
      searchParams.set("filters", JSON.stringify(filters))
      locationSearch = `?${searchParams.toString()}`
    } else if (newValue === 1) {
      const paymentFilters = {
        dateField: "sst_reception_time",
        startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
      }
      const searchParams = new URLSearchParams(locationSearch)
      searchParams.set("filters", JSON.stringify(paymentFilters))
      locationSearch = `?${searchParams.toString()}`
    }

    navigate(
      `/os/reporting/${
        ["kpis", "payment", "reservations"][newValue]
      }${locationSearch}`,
    )
  }
  const [snackMessage, setSnackMessage] = useState({
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info" | undefined,
  })

  const mode = useContext(ModeContext)

  return (
    <OsLayout
      selectedMenuItemLabelKey={subPage === "kpis" ? "KPIs" : "reporting"}
      hideMenu={mode === "embedded"}
    >
      <Stack direction="column" spacing={2}>
        <Stack
          direction="column"
          spacing={4}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            px: 2,
            pt: 2,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body1" fontWeight="bold">
                {t("reporting")}
              </Typography>
            </Stack>
            <Typography variant="body2">
              {t("reporting.moduleDescription")}
            </Typography>
          </Box>

          <Tabs
            value={SubPageTabMap[subPage]}
            onChange={handleTabChange}
            sx={{
              height: 60,
            }}
          >
            <Tab
              label={t("reportKPIs")}
              icon={<SpeedIcon />}
              iconPosition="start"
              sx={{
                display: subPage === "kpis" ? "inherit" : "none",
              }}
            />

            <Tab
              label={t("payments")}
              icon={<PaymentIcon />}
              iconPosition="start"
              sx={{
                display: subPage !== "kpis" ? "inherit" : "none",
              }}
            />
            <Tab
              label={t("reservations")}
              icon={<AssessmentIcon />}
              iconPosition="start"
              sx={{
                display: subPage !== "kpis" ? "inherit" : "none",
              }}
            />
          </Tabs>
        </Stack>
        {!isReady && (
          <>
            <Stack direction="column" spacing={2}>
              <Skeleton variant="rectangular" width="100%" height={118} />
              <Skeleton variant="rectangular" width="100%" height={118 / 2} />
              <Skeleton variant="rectangular" width="100%" height={118} />
              <Skeleton variant="rectangular" width="100%" height={118 / 2} />
            </Stack>
          </>
        )}
        {isReady && (
          <Box p={3}>
            <Snackbar
              open={!!snackMessage.message}
              autoHideDuration={6000}
              onClose={() =>
                setSnackMessage({ message: "", severity: undefined })
              }
            >
              <Alert severity={snackMessage.severity}>
                {snackMessage.message}
              </Alert>
            </Snackbar>
            {
              {
                0: <OsReportingKPIs />,
                1: <OsReportingPaymentReservations />,
                // 1: <OsReportingPayment />,
                2: <OsReportingReservations />,
                "-1": <LoadingSpinner />,
              }[
                isUndefined(SubPageTabMap[subPage])
                  ? "-1"
                  : SubPageTabMap[subPage]
              ]
            }
          </Box>
        )}
      </Stack>
    </OsLayout>
  )
}

export default OsReportingPage

const LoadingSpinner = () => {
  return (
    <Box data-ui="UI-SL2W-095"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: 300,
      }}
    >
      <CircularProgress />
    </Box>
  )
}
